import { useCallback, useMemo } from 'react';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { HubOption } from 'dorian-shared/types/branch/HubOption';
import type { BorderCondition } from 'dorian-shared/types/branch/HubOption';
import { createDefaultBorderCondition } from '../hubStepDefaults';
import { SYSTEM_DEFAULT_BORDER_COLOR } from '../HubOptions/Border/BorderColorInput';

type OnChange = (changes: Partial<HubOption>) => void;

export function useBorderConditions(
  conditions: BorderCondition[],
  defaultColor: string | null,
  onChange: OnChange,
) {
  const addCondition = useCallback(() => {
    const seedColor = defaultColor ?? SYSTEM_DEFAULT_BORDER_COLOR;
    onChange({ borderConditions: [...conditions, createDefaultBorderCondition(seedColor)] });
  }, [conditions, onChange, defaultColor]);

  const removeCondition = useCallback(
    (uuid: string) => {
      onChange({ borderConditions: conditions.filter((c) => c.uuid !== uuid) });
    },
    [conditions, onChange],
  );

  const updateCondition = useCallback(
    (uuid: string, updated: BorderCondition) => {
      onChange({ borderConditions: conditions.map((c) => (c.uuid === uuid ? updated : c)) });
    },
    [conditions, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = conditions.findIndex((c) => c.uuid === active.id);
        const newIndex = conditions.findIndex((c) => c.uuid === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          onChange({ borderConditions: arrayMove(conditions, oldIndex, newIndex) });
        }
      }
    },
    [conditions, onChange],
  );

  const summary = useMemo((): string => {
    if (conditions.length === 0) return '';
    return `${conditions.length} override${conditions.length === 1 ? '' : 's'}`;
  }, [conditions]);

  return {
    addCondition, removeCondition, updateCondition, handleDragEnd, summary,
  };
}
